import 'core-js/stable'
import { createApp, defineAsyncComponent, nextTick } from 'vue'
import App from './App.vue'
import Axios from './axios'
import VueAxios from 'vue-axios'
import router from './router'
import { store } from './legacyStore'
import Buefy from 'buefy'
import money from 'v-money3'
import MockAdapter from './mock-adapter'
import _ from 'underscore'
import spinLoader from './components/SpinLoader'
import Multiselect from 'vue-multiselect'
import { VueExtensions } from '@/plugins/vue-extensions'
import mitt from 'mitt'
import useApi from './composables/useApi'
import { setupAuthV2 } from './components/auth/authSetup'
import ToastPlugin from 'vue-toast-notification'
import Popper from 'vue3-popper'
import Editor from '@tinymce/tinymce-vue'
import { PromiseDialog } from 'vue3-promise-dialog'
import { vMaska } from 'maska/vue'
import VueCookies from 'vue-cookies'
import PrimeVue from 'primevue/config'
import Material from '@primeuix/themes/material'
import { definePreset } from '@primeuix/themes'
import 'primeicons/primeicons.css'
import { setupGlobalErrorHandler, chunkReloadPolicy, unauthorizedPolicy } from '@/lib/error'
import { useGlobalUtils } from './helpers/globalUtils'

import '@/styles/primevue/datagrid.css'
import '@/styles/ag-grid.css'

import 'buefy/dist/buefy.css'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import 'vue3-pdf-app/dist/icons/main.css'
import 'vue-toast-notification/dist/theme-default.css'

export const app = createApp(App)
export const bus = mitt()

// Register plugins
app.use(VueAxios, Axios)
app.use(Buefy, { defaultIconPack: 'fas' })
// Material preset, primary color derived from the app's own brand color variable (--bcc-color-default)
// rather than any of PrimeVue's own palettes. Each shade is computed from that one variable via
// color-mix() (the same technique the Material preset's own tokens already use internally, e.g.
// highlight.background) - lighter shades mix in white, darker shades mix in black, 500 is the true
// base color - so hover/active/focus states still shade correctly instead of rendering as one flat color.
const BccPreset = definePreset(Material, {
  semantic: {
    primary: {
      50: 'color-mix(in srgb, var(--bcc-color-default) 10%, white)',
      100: 'color-mix(in srgb, var(--bcc-color-default) 20%, white)',
      200: 'color-mix(in srgb, var(--bcc-color-default) 35%, white)',
      300: 'color-mix(in srgb, var(--bcc-color-default) 50%, white)',
      400: 'color-mix(in srgb, var(--bcc-color-default) 75%, white)',
      500: 'var(--bcc-color-default)',
      600: 'color-mix(in srgb, var(--bcc-color-default) 88%, black)',
      700: 'color-mix(in srgb, var(--bcc-color-default) 74%, black)',
      800: 'color-mix(in srgb, var(--bcc-color-default) 60%, black)',
      900: 'color-mix(in srgb, var(--bcc-color-default) 46%, black)',
      950: 'color-mix(in srgb, var(--bcc-color-default) 32%, black)',
    },
  },
})

app.use(PrimeVue, {
  theme: {
    preset: BccPreset,
    options: { prefix: 'p', darkModeSelector: false, cssLayer: false },
  },
})
app.use(VueExtensions)
app.use(store)
app.use(router)
app.use(ToastPlugin)
app.use(PromiseDialog)
app.use(VueCookies)
app.use(money, {
  decimal: '.',
  thousands: ',',
  prefix: '$',
  precision: 2,
  masked: false,
})

// Global exception handler wiring. Full behavior of setupGlobalErrorHandler and its options (notify/
// policies/router) is documented on SetupGlobalErrorHandlerOptions's own JSDoc in @/lib/error
// — ctrl/cmd-click the import above to jump there. Does not replace globalUtils.js's handleApiErr,
// which callers still invoke explicitly for axios errors from try/catch blocks.
const { errorToast, warningToast, infoToast } = useGlobalUtils()

const TOAST_BY_NOTIFY_LEVEL = {
  error: errorToast,
  warn: warningToast,
  info: infoToast,
}

setupGlobalErrorHandler(app, {
  // Shows a message to the user via the toast matching its severity.
  notify: ({ message, level }) => TOAST_BY_NOTIFY_LEVEL[level](message),

  // App-specific reactions, each in its own file under @/lib/error/policies. The first one that applies handles the error.
  policies: [
    chunkReloadPolicy({ guardMs: 60 * 1000 }),
    unauthorizedPolicy({ router, axios: Axios }),
  ],

  // Failed lazy route imports only surface via router.onError, so the router must be handed over too.
  router,
})

// Register global components
app.component('multiselect', Multiselect)
app.component('spin-loader', spinLoader)
app.component('Popper', Popper)
app.component('Editor', Editor)

// the heavy ones load on demand, in their own chunks, the first time a page shows one; why not the usual import: .docs/p001.md
// the PDF viewer (vue3-pdf-app, ~1.5 MB)
app.component('PdfViewer', defineAsyncComponent(() => import(/* webpackChunkName: "pdf" */ 'vue3-pdf-app')))
// the grid (ag-grid, ~1.2 MB); ag-grid needs its modules registered before the first grid renders
app.component(
  'DataGrid',
  defineAsyncComponent(async () => {
    const [{ AgGridVue }, { ModuleRegistry, AllCommunityModule }] = await Promise.all([
      import(/* webpackChunkName: "grid" */ 'ag-grid-vue3'),
      import(/* webpackChunkName: "grid" */ 'ag-grid-community'),
    ])
    ModuleRegistry.registerModules([AllCommunityModule])
    return AgGridVue
  }),
)

app.directive('focus', {
  mounted(el) {
    el.focus()
  },
})

app.directive('maska', vMaska)

app.directive('clickoutside', {
  mounted(el, binding) {
    el.clickOutsideEvent = function (event) {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
    document.body.addEventListener('touchstart', el.clickOutsideEvent)
  },
  unmounted(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
    document.body.removeEventListener('touchstart', el.clickOutsideEvent)
  },
})

// #0612 V2 auth: the auth API client and the glue to the Vuex clientSession
setupAuthV2()

// Move filters to globalProperties
if (process.env.VUE_APP_ENABLE_MOCK) {
  MockAdapter(Axios)
}

const { api } = useApi()

/*app.mixin({
  methods: {
    deselect(selection, key) {
      selection.forEach((row) => {
        row[key] = false
      })
    },
    regenerateSelected(selected, statementDate, type, context, loaderKey, callback = () => null) {
      context[loaderKey] = true
      switch (type) {
        case 'asset-report':
          api.assetReports.queueReportsToGenerate(
            {
              period: statementDate,
              institutions: [...selected.map((r) => r.institutionId)],
              isRegenerating: 1,
            },
            (_) => {
              context[loaderKey] = false
              callback()
            }
          )
          break
        case 'asset-summary-report':
          api.assetReports.queueReportsToGenerate(
            {
              period: statementDate,
              institutions: [...selected.map((r) => r.institutionId)],
              type: 'AssetSummaryReport',
              isRegenerating: 1,
            },
            (_) => {
              context[loaderKey] = false
              callback()
            }
          )
          break
        case '8925-report':
          api.C8925Reports.queueReportsToGenerate(
            {
              period: statementDate,
              institutions: [...selected.map((r) => r.institutionId)],
              isRegenerating: 1,
            },
            (_) => {
              context[loaderKey] = false
              callback()
            }
          )
          break
      }
    },
    keys(object) {
      if (object === null || object === undefined) {
        return []
      }

      return Object.keys(object)
    },
    moment,
    parseInt,
    commaNumberDisplay(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    $inputColorScale(inputLength, scales) {
      let color = ''

      scales.map((scale) => {
        if (inputLength >= scale.min && inputLength < scale.max) {
          color = scale.color
        }
      })

      return color
    },
    checkbox(element, state) {
      element.checked = state
    },
    element_clicker(element) {
      const inputs = document.querySelectorAll(element)

      if (inputs.length !== 0) {
        const input = inputs[0]
        var evt = document.createEvent('MouseEvent')
        evt.initEvent('click', true, false)
        input.dispatchEvent(evt)
      }
    },
    getUploadDate(date) {
      return moment(date).utc()
    },
    csvToJson(csvText, headers) {
      let obj = papaparse.parse(csvText)
      return JSON.stringify(obj.data.slice(1))
    },
    downloadCSV(arrayOfObjects, filename = 'export.csv') {
      const keys = Object.keys(arrayOfObjects[0])
      const header = keys.join(',')

      const rows = arrayOfObjects.map((obj) => {
        return keys.map((key) => obj[key]).join(',')
      })

      const csv = [header, ...rows].join('\n')

      const blob = new Blob([csv], { type: 'text/csv' })

      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename

      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },

    formatDate(date, format) {
      return moment(date).utc().format(format)
    },
    formatDateNoTZ(date, format) {
      return moment(date).format(format)
    },
    formatDateInLocalTimezone(date, format) {
      return moment(date).tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format(format)
    },
    sortArrayOfObjects(array, key) {
      return _.sortBy(array, key)
    },
    sortByInArray(array, name) {
      return _.sortBy(array, name)
    },
    sortBy(object, name) {
      return _.sortBy(object, name)
    },
    floor(num) {
      return Math.floor(num)
    },
    trim,
    forceFormat(event) {
      // check if input value is a number value
      let isNumericInput
      if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)) {
        isNumericInput = true
      } else {
        isNumericInput = false
      }

      // check if input value is a modifier key ex: arrowLeft, arrowRight, capslock, shift ect
      let isModifierKey
      if (
        event.shiftKey === true ||
        event.keyCode === 35 ||
        event.keyCode === 36 ||
        event.keyCode === 8 ||
        event.keyCode === 9 ||
        event.keyCode === 13 ||
        event.keyCode === 46 ||
        (event.keyCode > 36 && event.keyCode < 41) ||
        ((event.ctrlKey === true || event.metaKey === true) && (event.keyCode === 65 || event.keyCode === 67 || event.keyCode === 86 || event.keyCode === 88 || event.keyCode === 90))
      ) {
        isModifierKey = true
      } else {
        isModifierKey = false
      }

      // if inputed value is not a modifier or numeric strip it
      if (!isNumericInput && !isModifierKey) {
        event.preventDefault()
      }
    },
    formatPhoneInput(event) {
      // check if input value is a modifier key ex: arrowLeft, arrowRight, capslock, shift ect
      let isModifierKey
      if (
        event.shiftKey === true ||
        event.keyCode === 35 ||
        event.keyCode === 36 ||
        event.keyCode === 8 ||
        event.keyCode === 9 ||
        event.keyCode === 13 ||
        event.keyCode === 46 ||
        (event.keyCode > 36 && event.keyCode < 41) ||
        ((event.ctrlKey === true || event.metaKey === true) && (event.keyCode === 65 || event.keyCode === 67 || event.keyCode === 86 || event.keyCode === 88 || event.keyCode === 90))
      ) {
        isModifierKey = true
      } else {
        isModifierKey = false
      }

      // dont change anything if modifier key inputed
      // eslint-disable-next-line block-spacing
      if (isModifierKey) {
        return
      }

      // get the input element and auto format value to (000) 000-0000
      const el = event.target
      const userInput = el.value.replace(/\D/g, '').substring(0, 10)
      const zip = userInput.substring(0, 3)
      const middle = userInput.substring(3, 6)
      const last = userInput.substring(6, 10)

      if (userInput.length > 6) {
        el.value = `(${zip}) ${middle}-${last}`
      } else if (userInput.length > 3) {
        el.value = `(${zip}) ${middle}`
      } else if (userInput.length > 0) {
        el.value = `(${zip}`
      }
    },
    openTab(tabName) {
      let i, x, tablinks
      x = document.getElementsByClassName('content-tab')
      for (i = 0; i < x.length; i++) {
        x[i].style.display = 'none'
      }
      tablinks = document.getElementsByClassName('tab')
      for (i = 0; i < x.length; i++) {
        tablinks[i].className = tablinks[i].className.replace(' is-active', '')
      }
      document.getElementById(tabName).style.display = 'block'
      // evt.currentTarget.className += ' is-active'
    },
    resetForm() {
      Object.assign(this.input, this.inputDefault)
    },
    setupForm(model, deleteId = true) {
      Object.assign(this.input, model)
      this.inputDefault = model
    },
    getDocumentPath(__document) {
      if (!__document || !__document.path || __document.path === undefined) {
        return ''
      }

      let path = __document.path

      // If the url contains clients but is not a client document then update the path
      if (path.includes('/clients') && __document.typeCode !== 'USR') {
        path = path.replace('api/documents/clients/', 'api/documents/')
      } else if (!path.includes('/clients') && __document.typeCode === 'USR') {
        // path = path.replace('api/documents/', 'api/documents/clients/')
      }

      return path
    },
    confirmDelete(event) {
      if (this.hasSelection) this.showDeleteModal = true
    },
    setPageTitle(title) {
      if (title && title.length > 0) {
        document.title = `${title} | ${this.$siteName}`
      } else {
        document.title = this.$siteName
      }
    },
  },
})*/

store.dispatch('updateActiveSite').then(() => {
  nextTick(() => {
    // Mount app
    app.mount('#app')
  })
})
